import { DeleteOutlineOutlined, Edit, Save } from '@mui/icons-material';
import {
  AlertColor,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import Notification from 'components/Notification';
import { useEffect, useState } from 'react';

interface Props {
  id?: number | string;
  pageResponse: any;
  isAdd: boolean;
  setOpenForm: (open: boolean) => void;
  timeslotRateMapping: any[];
  setTimeslotRateMapping: (data: any) => void;
  partOfDays: any[];
}

const TimeslotRateMappingRow = ({
  id = '',
  pageResponse,
  isAdd,
  setOpenForm,
  timeslotRateMapping,
  setTimeslotRateMapping,
  partOfDays,
}: Props) => {
  const [partOfDay, setPartOfDay] = useState<string>('');
  const [rate, setRate] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [errors, setErrors] = useState({
    partOfDay: false,
    rateRequired: false,
    negativeRate: false,
  });

  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const isEditable = isAdd || isEditMode;

  useEffect(() => {
    setPartOfDay(pageResponse.partOfDay ?? '');
    setRate(pageResponse.rate ?? null);
  }, [pageResponse]);

  useEffect(() => {
    if (!isEditable) {
      setErrors({ partOfDay: false, rateRequired: false, negativeRate: false });
      setMessage({ display: false, severity: null, message: '' });
    }
  }, [isEditable]);

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const validateFields = () => {
    const isPartOfDayEmpty = partOfDay === '';
    const isRateEmpty = rate === null;
    const isRateNegative = rate !== null && rate < 0;

    setErrors({ partOfDay: isPartOfDayEmpty, rateRequired: isRateEmpty, negativeRate: isRateNegative });

    return !(isPartOfDayEmpty || isRateEmpty || isRateNegative);
  };

  const isDuplicateEntry = () =>
    timeslotRateMapping.some(ts => ts.partOfDay === partOfDay && ts.partOfDay !== id);

  const handleSave = () => {
    if (!validateFields()) return;

    if (isDuplicateEntry()) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.duplicateCorporateRate,
      });
      return;
    }

    const selected = partOfDays.find(p => p.id === partOfDay);
    const updatedEntry = {
      ...(pageResponse.id && { id: pageResponse.id }),
      partOfDay,
      partOfDayName: selected?.name ?? pageResponse.partOfDayName ?? '',
      rate,
    };

    if (isAdd) {
      setOpenForm(false);
      setTimeslotRateMapping((prev: any) => [updatedEntry, ...prev]);
    } else {
      setTimeslotRateMapping((prev: any) => prev.map((item: any) => (item.partOfDay === id ? updatedEntry : item)));
      setIsEditMode(false);
    }
  };

  const handleDelete = () => {
    if (isAdd) {
      setOpenForm(false);
    } else {
      setTimeslotRateMapping((prev: any) => prev.filter((item: any) => item.partOfDay !== id));
    }
  };
  const preventNumberInputScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.target as HTMLInputElement;
    target.blur();
  };
  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item sx={{ width: '45%' }}>
          <FormControl size="small" sx={{ width: '-webkit-fill-available' }}>
            <InputLabel id="demo-simple-select-label">{strings.timeSlot_title_list}</InputLabel>
            <Select
              label={strings.timeSlot_title_list}
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              data-testid="select"
              disabled={!isEditable}
              value={partOfDay}
              onChange={(e: any) => {
                setPartOfDay(e.target.value);
                setRate(null);
                setErrors(prev => ({ ...prev, partOfDay: e.target.value === '' }));
              }}>
              {partOfDays.map((p: any) => (
                <MenuItem key={p.id} role="item" value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {errors.partOfDay && <Typography sx={{ fontSize: '10px', color: 'red' }}>{strings.requiredText}</Typography>}
        </Grid>

        <Grid item sx={{ width: '45%' }}>
          <TextField
            sx={{ width: '-webkit-fill-available' }}
            label={strings.corporateRate}
            size="small"
            required
            data-testid="text"
            id="outlined-required"
            value={rate}
            type="number"
            onWheel={preventNumberInputScroll}
            disabled={!isEditable}
            onChange={e => {
              const value = parseFloat(e.target.value);
              setRate(isNaN(value) ? null : value);
              setErrors({
                ...errors,
                rateRequired: isNaN(value),
                negativeRate: value < 0,
              });
            }}
            error={errors.rateRequired || errors.negativeRate}
          />
          {(errors.rateRequired || errors.negativeRate) && (
            <Typography sx={{ fontSize: 10, color: 'red', pt: 0.2 }}>
              {errors.rateRequired ? strings.requiredText : strings.lessThaZeroError}
            </Typography>
          )}
        </Grid>

        <Grid item sx={{ width: '5%' }}>
          <IconButton
            data-testid="add_btn"
            onClick={() => {
              if (isEditable) {
                handleSave();
              } else {
                setIsEditMode(true);
              }
            }}>
            {isEditable ? <Save color="primary" /> : <Edit color="primary" />}
          </IconButton>
        </Grid>
        <Grid item sx={{ width: '5%' }}>
          <IconButton onClick={handleDelete}>
            <DeleteOutlineOutlined color="primary" />
          </IconButton>
        </Grid>
      </Grid>
    </>
  );
};
export default TimeslotRateMappingRow;
